import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useFavorites } from '../contexts/FavoritesContext';
import { usePostingChooser } from '../contexts/PostingChooserContext';
import useScrollRestore from '../hooks/useScrollRestore';
import { formatMoney } from '../utils/formatters';
import RecentlyViewedCarousel from '../components/RecentlyViewedCarousel';
import { getAdTitle, getAdDescription } from '../utils/adI18n';
import { calculateDistance, formatDistance } from '../utils/distance';
import { getVisibleLiveMarketplaceAds } from '../utils/canonicalAdFilter';
import { SelectField } from '../components/SelectField';
import { VerifiedBoutiqueBadge, isVerifiedBoutique } from '../components/LadiesHubComponents';
import { VerifiedSellerBadge, isSellerVerified } from '../components/TrustBadges';
import PremiumSortSheet from '../components/PremiumSortSheet';
import UnifiedFilterSheet, { countActiveFilters } from '../components/UnifiedFilterSheet';
import '../components/UnifiedFilterSheet.css';
import useLocationFilter from '../hooks/useLocationFilter';
import PremiumSearchBar from '../components/PremiumSearchBar';
import { ProductCard, ProductGrid } from '../components/ProductGrid';
import ProductListCard from '../components/listings/ProductListCard';
import '../components/listings/ProductListCard.css';
import ViewToggle from '../components/ViewToggle';
import '../components/ViewToggle.css';
import useViewPreference from '../hooks/useViewPreference';
import { searchAds } from '../lib/searchAds';
import { trackSearchSubmit, trackFilterApply, trackFavoriteAdd } from '../lib/analytics';
import { guardImage, getCategoryPlaceholder } from '../lib/imageTrust';
import { getAdCoverImage } from '../lib/adImages';
import { getLocalizedLocationLabel } from '../components/AdInfoCard';
import RecentSearchChips from '../components/RecentSearchChips';
import { getRecentSearches, addRecentSearch, clearRecentSearches } from '../lib/recentSearches';
import { consumeWalletReturnContext, savePageViewState, loadPageViewState } from '../lib/routeStateRestore';
import { getHiddenListingIds } from '../utils/hiddenListings';
import BoostTimerBadge from '../components/BoostTimerBadge';
import ShopCategoryGrid from '../components/ShopCategoryGrid';
import { generateSafeForYouFeed } from '../utils/safeForYou';
import { extractPreferredCategories, getRecentCategories } from '../utils/forYou';
import { MapPin, Heart, ChevronLeft, ChevronRight, ImageIcon, SlidersHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import InlineError from '../components/InlineError';
import { dedupeAdsById } from '../utils/dedupeAds';
import { useUnifiedSearch } from '../hooks/useUnifiedSearch';
import { resetAllListingFilters } from '../utils/filterReset';
import { resolveCountryLabel, resolveCityLabel } from '../utils/locationLabels';
import { sortAdsByPrice, parsePrice, isPriceAsc, isPriceDesc } from '../utils/priceUtils';
import './Shop.css';

// Check if low bandwidth mode is enabled
const isLowBandwidthMode = () => {
  return localStorage.getItem('lowBandwidthMode') === 'true';
};

function Shop() {
  const { t, i18n } = useTranslation();
  const { lang } = useLanguage();
  const isArabic = i18n.language?.startsWith('ar');
  const isRTL = isArabic;
  const { token, user } = useAuth();
  const { isFavorite, toggleFavorite } = useFavorites();
  // iter290 — Same posting chooser as the orange + button.
  const { openChooser } = usePostingChooser();
  const navigate = useNavigate();
  const location = useLocation();

  // Location data for localized filter chip labels — uses the shared
  // resolver so raw slugs like "egypt" can never leak into the UI (see
  // utils/locationLabels.js). Same logic as Home.js — one source of truth.
  const { countries: locationCountries } = useLocationFilter();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ads, setAds] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || 'all');
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [categoryGridExpanded, setCategoryGridExpanded] = useState(true);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [sortBy, setSortBy] = useState('newest');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [locationFilter, setLocationFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState(searchParams.get('country') || '');
  const [cityFilter, setCityFilter] = useState(searchParams.get('city') || '');
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  useScrollRestore(!loading);
  const [showFilters, setShowFilters] = useState(false);
  const [sortOpen, setSortOpen] = useState(false); // Premium sort sheet state
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // iter308 — total count of ads matching the current query as reported
  // by /api/ads `total` field. This is the canonical "shop visible"
  // count that the Admin Overview uses; we display it instead of the
  // paginated subset length so the Shop label matches what admins see.
  const [totalCount, setTotalCount] = useState(0);
  const [buyerLocation, setBuyerLocation] = useState(null);
  const [showDistance, setShowDistance] = useState(false);
  // locationLoading state retired — the geolocation spinner now lives inside the shared LocationSelector.
  const [recentSearches, setRecentSearches] = useState([]);
  const [isRestoringViewState, setIsRestoringViewState] = useState(true);
  const pendingScrollRestoreRef = useRef(null);
  const [cityFilterActive, setCityFilterActive] = useState(false);
  const searchUserKey = user?.user_id || user?.id || 'guest';
  const lowBandwidth = isLowBandwidthMode();
  const pageSize = lowBandwidth ? 20 : 40;

  // View preference (list/grid toggle)
  const { view: listingView, setView: setListingView } = useViewPreference('shop', user?.user_id);

  // Shop filter defaults
  const SHOP_DEFAULT_FILTERS = {
    country: '',
    city: '',
    priceMin: '',
    priceMax: '',
    sort: 'newest',
    deliveryAvailable: false,
  };
  
  // Unified filters state for UnifiedFilterSheet
  const [shopFilters, setShopFilters] = useState({
    country: searchParams.get('country') || '',
    city: searchParams.get('city') || '',
    priceMin: searchParams.get('priceMin') || '',
    priceMax: searchParams.get('priceMax') || '',
    sort: searchParams.get('sort') || 'newest',
    deliveryAvailable: searchParams.get('deliveryAvailable') === 'true',
  });

  // Chip label resolver — declared here (after shopFilters) so the closure
  // can safely read shopFilters.country to pick the correct city list.
  const getLocationLabel = useCallback((type, key) => {
    if (type === 'country') return resolveCountryLabel(key, isArabic, locationCountries);
    const cityList = (locationCountries.find((c) => c.key === shopFilters.country)?.cities) || [];
    return resolveCityLabel(key, isArabic, cityList);
  }, [locationCountries, isArabic, shopFilters.country]);

  const SHOP_VIEW_STATE_KEY = 'shop';

  // NO auto-geolocation on mount - only on explicit user action
  // Check if location is already cached in sessionStorage
  useEffect(() => {
    const cached = sessionStorage.getItem('buyerLocation');
    if (cached) {
      try {
        const location = JSON.parse(cached);
        setBuyerLocation(location);
        setShowDistance(true);
      } catch {
        // Invalid cache, ignore
      }
    }
  }, []);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setRecentSearches(getRecentSearches(searchUserKey));
  }, [searchUserKey]);

  useEffect(() => {
    const restoreContext = consumeWalletReturnContext(location.pathname);
    if (restoreContext) {
      const saved = loadPageViewState(SHOP_VIEW_STATE_KEY);
      if (saved) {
        if (typeof saved.selectedCategory === 'string') setSelectedCategory(saved.selectedCategory);
        if (typeof saved.searchQuery === 'string') setSearchQuery(saved.searchQuery);
        if (typeof saved.sortBy === 'string') setSortBy(saved.sortBy);
        if (saved.priceRange && typeof saved.priceRange === 'object') {
          setPriceRange({
            min: saved.priceRange.min || '',
            max: saved.priceRange.max || '',
          });
        }
        if (typeof saved.locationFilter === 'string') setLocationFilter(saved.locationFilter);
      }

      if (typeof restoreContext.scrollTop === 'number') {
        pendingScrollRestoreRef.current = restoreContext.scrollTop;
      }
    }

    setIsRestoringViewState(false);
  }, [location.pathname]);

  useEffect(() => {
    savePageViewState(SHOP_VIEW_STATE_KEY, {
      selectedCategory,
      searchQuery,
      sortBy,
      priceRange,
      locationFilter,
    });
  }, [selectedCategory, searchQuery, sortBy, priceRange, locationFilter]);

  useEffect(() => {
    if (isRestoringViewState) return;
    setPage(1);
    fetchAds(1, true);
  }, [selectedCategory, selectedSubcategory, searchQuery, sortBy, isRestoringViewState, cityFilterActive]);

  useEffect(() => {
    if (isRestoringViewState || loading) return;
    if (pendingScrollRestoreRef.current == null) return;

    const container = document.querySelector('.main-content');
    if (container) {
      container.scrollTop = pendingScrollRestoreRef.current;
    }
    pendingScrollRestoreRef.current = null;
  }, [loading, ads.length, isRestoringViewState]);

  // Update URL when category changes
  useEffect(() => {
    const params = new URLSearchParams();
    if (selectedCategory && selectedCategory !== 'all') {
      params.set('category', selectedCategory);
    }
    if (searchQuery) {
      params.set('search', searchQuery);
    }
    setSearchParams(params);
  }, [selectedCategory, searchQuery, setSearchParams]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchAds = async (pageNum = 1, reset = false) => {
    if (pageNum === 1) setLoading(true);
    setFetchError(false);
    
    console.log('[Shop] fetchAds called with shopFilters:', {
      country: shopFilters.country,
      city: shopFilters.city,
      priceMin: shopFilters.priceMin,
      priceMax: shopFilters.priceMax,
      sort: shopFilters.sort,
    });
    
    try {
      const data = await searchAds({
        pageNum,
        pageSize,
        selectedCategory,
        selectedSubcategory,
        searchQuery,
        sortBy: shopFilters.sort || sortBy,
        priceRange: {
          min: shopFilters.priceMin || priceRange.min,
          max: shopFilters.priceMax || priceRange.max,
        },
        locationFilter: cityFilterActive && user?.city ? user.city : locationFilter,
        country: shopFilters.country || '',
        city: shopFilters.city || '',
        // iter322 Phase B — Strict manual filter. Backend hard-rejects
        // ads whose structured country doesn't match (no fallback to
        // free-form city/location text). Egypt filter must NEVER bleed
        // into Sudan ads and vice versa.
        locationStrict: Boolean(shopFilters.country),
        // iter328 — Delivery availability filter.
        deliveryAvailable: Boolean(shopFilters.deliveryAvailable),
        strict: true,
      });

      console.log('[Shop] API response:', { adsCount: data.ads?.length, total: data.total });

      const sortedAds = data.ads || [];
      // iter308 — Use backend `total` (full match count for the current
      // query) as the canonical listings count; falls back to current
      // page length only when backend omits the field (defensive only).
      if (typeof data.total === 'number') {
        setTotalCount(data.total);
      } else if (reset) {
        setTotalCount(sortedAds.length);
      }
        // Sorting is now handled by backend via sort param
        
        // iter320 — Canonical visible-live-ads filter (also dedupes).
        // Same helper used by Shop and the Admin dashboard mismatch
        // detector — guarantees Home renders exactly what Admin
        // counts as "Live Shop Ads".
        if (reset) {
          setAds(getVisibleLiveMarketplaceAds(sortedAds));
        } else {
          setAds(prev => getVisibleLiveMarketplaceAds([...prev, ...sortedAds]));
        }
        
        setHasMore(sortedAds.length === pageSize);
        setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch ads:', error);
      if (ads.length === 0) setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (categoryKey) => {
    setSelectedCategory(categoryKey);
    setSelectedSubcategory(null);
    if (categoryKey !== 'all') setCategoryGridExpanded(false);
  };

  const handleSubcategoryChange = (subKey) => {
    setSelectedSubcategory(subKey);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      fetchAds(page + 1, false);
    }
  };

  // Unified filter handlers for UnifiedFilterSheet
  const handleFiltersChange = useCallback((newFilters) => {
    console.log('[Shop] Filters changed:', newFilters);
    setShopFilters(newFilters);
    // Sync to individual state for legacy code
    setPriceRange({ min: newFilters.priceMin || '', max: newFilters.priceMax || '' });
    setSortBy(newFilters.sort || 'newest');
    setCountryFilter(newFilters.country || '');
    setCityFilter(newFilters.city || '');
  }, []);

  const applyFilters = useCallback(() => {
    console.log('[Shop] Applying filters, current shopFilters:', shopFilters);
    setPage(1);
    // fetchAds will be triggered by the useEffect watching shopFilters
    setShowFilters(false);
    trackFilterApply({ ...shopFilters, category: selectedCategory, source: 'shop' });
  }, [shopFilters, selectedCategory]);

  // Clear all filters — ROOT-CAUSE fix: uses ONE shared reset util with Home.
  // Wipes unified filter object, legacy state (incl. cityFilterActive and
  // showDistance), category/subcategory, search query, URL params,
  // sessionStorage caches (page view + buyerLocation), paging, and closes
  // the sheet. Single source of truth.
  const clearFilters = useCallback(() => {
    console.log('[Shop] Clearing all filters');
    resetAllListingFilters({
      setters: {
        setFilters: setShopFilters,
        setPriceRange,
        setLocationFilter,
        setCountryFilter,
        setCityFilter,
        setCityFilterActive,
        setShowDistance,
        setBuyerLocation,
        setSortBy,
        setSelectedCategory,
        setSelectedSubcategory,
        setSearchQuery,
        setPage,
        setShowFilters,
      },
      pageKey: SHOP_VIEW_STATE_KEY,
      searchParams,
      setSearchParams,
      defaults: SHOP_DEFAULT_FILTERS,
    });
  }, [searchParams, setSearchParams]);

  // Refetch when shop filters change
  useEffect(() => {
    console.log('[Shop] useEffect triggered, fetching with filters:', shopFilters);
    fetchAds(1, true);
  }, [shopFilters.country, shopFilters.city, shopFilters.priceMin, shopFilters.priceMax, shopFilters.sort, shopFilters.deliveryAvailable]);

  // Distance "off" toggle — invoked by clearFilters / chip clear. Geolocation
  // request itself now lives in the shared LocationSelector; this remaining
  // helper just wipes the cached location when the user backs out.
  // (Inlined where used; kept around in case future surfaces need a manual off path.)

  // Get image URL (use thumbnail for low bandwidth) with trust guard
  // iter274 — delegate field-name resolution to the canonical normaliser
  // so every legacy field (image_urls, photos, imageUrl, photo_url,
  // media, thumbnail, …) is supported. Only ads with NO valid
  // persistent image fall through to the category placeholder.
  const getImageUrl = (ad) => {
    const cover = getAdCoverImage(ad);
    if (!cover) return getCategoryPlaceholder(ad.category_id || ad.category);
    if (lowBandwidth && cover.includes('/uploads/')) {
      return cover;
    }
    return guardImage(cover, ad.category_id || ad.category);
  };

  // Hidden-listings filter — applied before search/sort so hidden ads
  // never reach the user-visible pipeline.
  const adsAfterHidden = useMemo(() => {
    if (!ads || ads.length === 0) return ads;
    const hiddenIds = getHiddenListingIds(searchUserKey);
    if (hiddenIds.length === 0) return ads;
    return ads.filter(a => !hiddenIds.includes(a.ad_id));
  }, [ads, searchUserKey]);

  // ═══════════════════════════════════════════════════════════
  // UNIFIED SEARCH PIPELINE — filter FIRST, sort AFTER (per spec)
  // normalize → filter → dedupe (shared with Home, LadiesHub, etc.)
  // ═══════════════════════════════════════════════════════════
  const filteredAds = useUnifiedSearch(adsAfterHidden, searchQuery);

  // Client-side sort pipeline — single source of truth per sort mode:
  //   - 'price_low' / 'price_high' / 'newest'  → backend already sorts
  //     these (purely, no tier interleaving), so we DO NOT re-sort
  //     client-side (avoiding double-sort + pagination drift).
  //   - 'salary_low' / 'salary_high' / 'year_newest' / 'area_size' →
  //     backend doesn't natively know these category-specific fields;
  //     client sort is the only source of truth.
  //   - Free items (price === 0) sort by their numeric price like any
  //     other item in price modes — no implicit free-first boost.
  const displayAds = useMemo(() => {
    if (!filteredAds || filteredAds.length === 0) return filteredAds || [];
    const items = [...filteredAds];
    switch (sortBy) {
      case 'salary_low':
        return sortAdsByPrice(items, 'asc', (x) => x.salary ?? x.price);
      case 'salary_high':
        return sortAdsByPrice(items, 'desc', (x) => x.salary ?? x.price);
      case 'year_newest':
        items.sort((a, b) => (parsePrice(b.car_attrs?.year) ?? -1) - (parsePrice(a.car_attrs?.year) ?? -1));
        return items;
      case 'area_size':
        items.sort((a, b) => (parsePrice(b.property_attrs?.area_size) ?? -1) - (parsePrice(a.property_attrs?.area_size) ?? -1));
        return items;
      // 'price_low' / 'price_high' / 'newest' / 'views' → trust backend
      // ordering; client must NOT re-sort or we get pagination drift.
      default:
        return items;
    }
  }, [filteredAds, sortBy]);


  // Handle image load error - show neutral placeholder
  const handleImageError = (e) => {
    e.target.style.display = 'none';
    // Show the placeholder icon instead
    const placeholder = e.target.nextElementSibling;
    if (placeholder) {
      placeholder.style.display = 'flex';
    }
  };

  // Handle successful image load
  const handleImageLoad = (e) => {
    e.target.parentElement.classList.add('loaded');
  };

  const ChevronIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div className="shop-page">
      {/* ========================================
          STICKY HEADER STACK - Search FIRST, then Chips
      ======================================== */}
      <div className="shop-sticky-header" data-testid="shop-sticky-header">
        {/* Search & Filter Bar - Sticky Row 1 */}
        <div className="shop-toolbar" data-testid="shop-toolbar">
          <button
            className="shop-filter-btn"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="shop-filter-toggle"
          >
            <SlidersHorizontal size={18} />
          </button>

          <PremiumSearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={(q) => {
              const trimmed = String(q || '').trim();
              setSearchQuery(trimmed);
              if (trimmed) {
                addRecentSearch(trimmed, searchUserKey);
                setRecentSearches(getRecentSearches(searchUserKey));
                trackSearchSubmit(trimmed, { source: 'shop', category: selectedCategory });
              }
            }}
            showFilters={false}
            showCamera={false}  /* RE-ENABLE when: 1) real product photos on listings, 2) CLIP embeddings indexed, 3) test images return relevant results */
            placeholder={t('shopPage.searchPlaceholder')}
            dir={isRTL ? 'rtl' : 'ltr'}
            hasActiveFilters={sortBy !== 'newest' || priceRange.min || priceRange.max || locationFilter}
            userId={searchUserKey}
          />
        </div>

        <RecentSearchChips
          items={recentSearches}
          onSelect={(term) => setSearchQuery(term)}
          onClear={() => {
            clearRecentSearches(searchUserKey);
            setRecentSearches([]);
          }}
          isRTL={isRTL}
          title={t('shopPage.recentSearches')}
        />
      </div>

      {/* Shop by Category — collapsible */}
      {categoryGridExpanded ? (
        <ShopCategoryGrid
          categories={categories}
          selectedCategory={selectedCategory}
          selectedSubcategory={selectedSubcategory}
          onSelectCategory={handleCategoryChange}
          onSelectSubcategory={handleSubcategoryChange}
        />
      ) : (
        <section className="shop-collapsed-cats">
          <div className="shop-collapsed-header">
            <span className="shop-collapsed-title">{t('shopPage.categories')}</span>
            <button
              className="shop-expand-btn"
              onClick={() => { setCategoryGridExpanded(true); setSelectedCategory('all'); }}
            >
              {t('shopPage.changeCategory')}
            </button>
          </div>
          <div className="shop-quick-chips">
            {categories.filter(c => c.key !== 'all' && c.key !== 'recommended' && !c.special).slice(0, 8).map(c => (
              <button
                key={c.key}
                className={`shop-quick-chip ${c.key === selectedCategory ? 'shop-quick-chip-active' : ''}`}
                onClick={() => handleCategoryChange(c.key)}
              >
                {isRTL ? (c.name_ar || c.name_en) : (c.name_en || c.name_ar)}
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Active Filter Summary */}
      {selectedCategory !== 'all' && (
        <div className="shop-filter-summary" data-testid="filter-summary">
          <span className="sfs-item sfs-cat">
            {categories.find(c => c.key === selectedCategory)?.[isRTL ? 'name_ar' : 'name_en'] || selectedCategory}
          </span>
          {selectedSubcategory && <span className="sfs-dot">·</span>}
          {selectedSubcategory && <span className="sfs-item">{selectedSubcategory}</span>}
          <span className="sfs-dot">·</span>
          <span className="sfs-item">{
            [{key:'newest',en:'Newest',ar:'الأحدث'},{key:'price_low',en:'Price Low',ar:'الأقل'},{key:'price_high',en:'Price High',ar:'الأعلى'},{key:'views',en:'Popular',ar:'رائج'}]
              .find(s => s.key === sortBy)?.[isRTL ? 'ar' : 'en'] || sortBy
          }</span>
        </div>
      )}

      {/* Sort Chips Bar — category-specific */}
      <section className="shop-sort-bar" data-testid="shop-sort-bar">
        <div className="shop-sort-chips">
          {(() => {
            const cat = selectedCategory.toLowerCase();
            let opts;
            if (cat.includes('job')) {
              opts = [{key:'newest',en:'Newest',ar:'الأحدث'},{key:'views',en:'Relevant',ar:'الأكثر صلة'},{key:'price_low',en:'Salary Low',ar:'الراتب: الأقل'},{key:'price_high',en:'Salary High',ar:'الراتب: الأعلى'}];
            } else if (cat.includes('estate') || cat.includes('re_')) {
              opts = [{key:'newest',en:'Newest',ar:'الأحدث'},{key:'price_low',en:'Price Low',ar:'السعر: الأقل'},{key:'price_high',en:'Price High',ar:'السعر: الأعلى'},{key:'views',en:'Area Size',ar:'المساحة'}];
            } else if (cat.includes('vehicle') || cat.includes('car')) {
              opts = [{key:'newest',en:'Newest',ar:'الأحدث'},{key:'price_low',en:'Price Low',ar:'السعر: الأقل'},{key:'price_high',en:'Price High',ar:'السعر: الأعلى'},{key:'views',en:'Year Newest',ar:'الأحدث سنة'}];
            } else {
              opts = [{key:'newest',en:'Newest',ar:'الأحدث'},{key:'price_low',en:'Price Low',ar:'السعر: الأقل'},{key:'price_high',en:'Price High',ar:'السعر: الأعلى'},{key:'views',en:'Popular',ar:'الأكثر شعبية'}];
            }
            return opts.map(s => (
              <button key={s.key} className={`shop-sort-chip ${(shopFilters.sort || sortBy) === s.key ? 'shop-sort-chip-active' : ''}`}
                onClick={() => {
                  // Keep legacy sortBy and unified shopFilters.sort in sync so
                  // the filter sheet sees an active filter (enables Clear) and
                  // the backend receives the selected sort.
                  setSortBy(s.key);
                  setShopFilters(prev => ({ ...prev, sort: s.key }));
                  setPage(1);
                }} data-testid={`sort-${s.key}`}>
                {isRTL ? s.ar : s.en}
              </button>
            ));
          })()}
          {user?.city && (
            <button
              className={`shop-sort-chip ${cityFilterActive ? 'shop-sort-chip-active' : ''}`}
              onClick={() => { setCityFilterActive(prev => !prev); setPage(1); }}
              data-testid="filter-city"
            >
              <MapPin size={13} style={{ marginRight: isRTL ? 0 : 3, marginLeft: isRTL ? 3 : 0 }} />
              {isRTL ? `جديد في ${user.city}` : `New in ${user.city}`}
            </button>
          )}
        </div>
      </section>

      {/* Unified Filter Modal - Global location filtering */}
      <UnifiedFilterSheet
        open={showFilters}
        onOpenChange={setShowFilters}
        filters={shopFilters}
        onFiltersChange={handleFiltersChange}
        onApply={applyFilters}
        onClear={clearFilters}
        onCoordsResolved={(coords) => {
          // The shared LocationSelector resolved real coordinates — keep the
          // existing distance-on-cards behaviour wired up. Country/city are
          // already auto-filled by the selector via onCountryChange/onCityChange.
          if (!coords) return;
          try {
            sessionStorage.setItem('buyerLocation', JSON.stringify(coords));
          } catch { /* noop */ }
          setBuyerLocation(coords);
          setShowDistance(true);
        }}
        title={t('shopPage.filterTitle')}
        defaultFilters={SHOP_DEFAULT_FILTERS}
      >
        {({ draft, setDraft }) => (
          <>
            {/* Sort By */}
            <div className="ufs-section">
              <label className="ufs-section-label">{t('shopPage.sortByLabel')}</label>
              <select
                value={draft.sort || 'newest'}
                onChange={(e) => setDraft('sort', e.target.value)}
                className="ufs-select"
                data-testid="sort-select"
              >
                <option value="newest">{t('shopPage.sortNewest')}</option>
                <option value="price_low">{t('shopPage.sortPriceLow')}</option>
                <option value="price_high">{t('shopPage.sortPriceHigh')}</option>
                <option value="views">{t('shopPage.sortPopular')}</option>
              </select>
            </div>
            
            {/* Price Range */}
            <div className="ufs-section">
              <label className="ufs-section-label">{t('shopPage.priceRangeLabel')}</label>
              <div className="ufs-price-row">
                <input
                  type="number"
                  placeholder={t('shopPage.min')}
                  value={draft.priceMin || ''}
                  onChange={(e) => setDraft('priceMin', e.target.value)}
                  className="ufs-price-input"
                />
                <span className="ufs-price-sep">—</span>
                <input
                  type="number"
                  placeholder={t('shopPage.max')}
                  value={draft.priceMax || ''}
                  onChange={(e) => setDraft('priceMax', e.target.value)}
                  className="ufs-price-input"
                />
              </div>
            </div>
            
            {/* iter328 — Delivery availability toggle. When ON, the
                Shop only shows ads where the seller flagged delivery
                as available. */}
            <div className="ufs-section">
              <label className="ufs-section-label">
                {isArabic ? 'التوصيل' : 'Delivery'}
              </label>
              <button
                type="button"
                role="switch"
                aria-checked={Boolean(draft.deliveryAvailable)}
                className={`ufs-delivery-toggle ${draft.deliveryAvailable ? 'is-on' : ''}`}
                onClick={() => setDraft('deliveryAvailable', !draft.deliveryAvailable)}
                data-testid="filter-delivery-available-toggle"
              >
                <span className="ufs-delivery-toggle-text">
                  <span className="ufs-delivery-toggle-icon" aria-hidden="true">🚚</span>
                  {isArabic ? 'التوصيل متاح' : 'Delivery available'}
                </span>
                <span className="ufs-delivery-toggle-knob" />
              </button>
            </div>

            {/* Distance section retired — "Use my location" lives inside the
                shared LocationSelector below and drives the same buyerLocation
                + showDistance state via onCoordsResolved. */}
          </>
        )}
      </UnifiedFilterSheet>

      {/* Active Filter Summary */}
      {(shopFilters.sort !== 'newest' || shopFilters.priceMin || shopFilters.priceMax || shopFilters.country || shopFilters.city || showDistance) && (
        <div className="shop-active-filters" data-testid="active-filters">
          {shopFilters.country && (
            <span className="saf-chip saf-chip-green" data-testid="filter-country-chip">
              {getLocationLabel('country', shopFilters.country)}
            </span>
          )}
          {shopFilters.city && (
            <span className="saf-chip saf-chip-green">{getLocationLabel('city', shopFilters.city)}</span>
          )}
          {shopFilters.sort !== 'newest' && (
            <span className="saf-chip">
              {shopFilters.sort === 'price_low' ? (t('shopPage.sortPriceLowShort')) :
               shopFilters.sort === 'price_high' ? (t('shopPage.sortPriceHighShort')) :
               shopFilters.sort === 'views' ? (t('shopPage.sortPopularShort')) : shopFilters.sort}
            </span>
          )}
          {shopFilters.priceMin && (
            <span className="saf-chip">{t('shopPage.min')} {shopFilters.priceMin}</span>
          )}
          {shopFilters.priceMax && (
            <span className="saf-chip">{t('shopPage.max')} {shopFilters.priceMax}</span>
          )}
          {showDistance && (
            <span className="saf-chip">{t('shopPage.distanceOn')}</span>
          )}
          <button className="saf-clear" onClick={clearFilters}>
            {t('shopPage.clear')}
          </button>
        </div>
      )}

      {/* Results Header */}
      <div className="results-info" id="shop-ads-area">
        <div className="results-info-row">
          <div>
            <span className="results-info-title">
              {(sortBy !== 'newest' || priceRange.min || priceRange.max || locationFilter)
                ? (t('shopPage.filteredResults'))
                : selectedCategory !== 'all' 
                  ? categories.find(c => c.key === selectedCategory)?.[isRTL ? 'name_ar' : 'name_en'] || selectedCategory
                  : (t('shopPage.browseListings'))}
            </span>
            {/* iter318 — Public Shop UI no longer surfaces a live-ad count.
                Admin Overview remains the source of truth for the exact
                number (`ads.public_live` from `_build_public_live_ads_query`).
                Hiding the count on the public surface eliminates the
                snapshot-race confusion (Shop 40 vs Admin 41) without
                touching the canonical backend query. */}
          </div>
          {/* View Toggle */}
          <ViewToggle
            view={listingView}
            onViewChange={setListingView}
            size="sm"
            disabled={loading}
          />
        </div>
      </div>

      {/* Products Grid (Main Content - 80-90% of screen) */}
      <div className="products-container">
        {loading && page === 1 ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>{t('loading')}</p>
          </div>
        ) : fetchError && displayAds.length === 0 ? (
          <InlineError onRetry={() => fetchAds(1, true)} />
        ) : displayAds.length === 0 ? (
          (() => {
            // iter279 — empty-state with 3 distinct, warmer copies:
            // 1. SEARCH: user typed something → "No matching ads found"
            // 2. FILTERS too narrow: country/city/price/sub-category active
            //    (but NO search text) → "No ads match your current filters"
            // 3. DEFAULT (category strip is "all" or a single category with
            //    no ads yet) → "No ads yet in this section"
            const hasSearchQuery = (searchQuery || '').trim().length > 0;
            const hasFilters = !!(
              shopFilters.country || shopFilters.city ||
              shopFilters.priceMin || shopFilters.priceMax ||
              selectedSubcategory
            );

            let mode = 'empty';
            if (hasSearchQuery) mode = 'search';
            else if (hasFilters) mode = 'filters';

            // Copy table — kept inline so the strings are easy to audit
            // against the operator-supplied spec.
            const COPY = {
              search: {
                icon: '🔍',
                title: isArabic ? 'لم يتم العثور على إعلانات مطابقة' : 'No matching ads found',
                text: isArabic ? 'جرّب كلمة بحث أخرى أو تصفح الأقسام الموجودة.' : 'Try a different keyword or browse the categories below.',
                primaryLabel: isArabic ? 'مسح البحث' : 'Clear search',
                primaryAction: () => { setSearchQuery(''); },
                secondaryLabel: isArabic ? 'تصفح الأقسام' : 'Explore categories',
                secondaryAction: () => { setSelectedCategory('all'); setSearchQuery(''); },
              },
              filters: {
                icon: '🎛️',
                title: isArabic ? 'لا توجد إعلانات تطابق الفلاتر الحالية' : 'No ads match your current filters',
                text: isArabic ? 'جرّب مسح الفلاتر أو تعديلها لعرض المزيد من النتائج.' : 'Try clearing or changing your filters to see more results.',
                primaryLabel: isArabic ? 'مسح الفلاتر' : 'Clear filters',
                primaryAction: () => { clearFilters(); },
                secondaryLabel: isArabic ? 'تصفح الأقسام' : 'Explore categories',
                secondaryAction: () => { clearFilters(); setSelectedCategory('all'); },
              },
              empty: {
                icon: '🛍️',
                title: isArabic ? 'لا توجد إعلانات في هذا القسم بعد' : 'No ads yet in this section',
                text: isArabic ? 'ستظهر الإعلانات الجديدة هنا قريباً. يمكنك تصفح أقسام أخرى أو كن أول من ينشر إعلاناً.' : 'New listings will appear here soon. You can explore other categories or be the first to post an ad.',
                primaryLabel: isArabic ? 'انشر إعلاناً' : 'Post an ad',
                // iter290 — Open the SAME posting chooser sheet the
                // orange + button opens (PostingChooserContext.openChooser).
                // The legacy `/post-ad` route never existed and produced
                // a black blank page on production.
                primaryAction: () => { openChooser(null); },
                secondaryLabel: isArabic ? 'تصفح الأقسام' : 'Explore categories',
                secondaryAction: () => { setSelectedCategory('all'); },
              },
            };

            const c = COPY[mode];
            return (
              <div className="empty-state" data-testid="shop-empty-state" data-mode={mode}>
                <div className="empty-icon" aria-hidden="true">{c.icon}</div>
                <h3 data-testid="shop-empty-title">{c.title}</h3>
                <p data-testid="shop-empty-text">{c.text}</p>
                <div className="empty-actions">
                  <button
                    type="button"
                    className="btn-reset"
                    onClick={c.primaryAction}
                    data-testid="shop-empty-primary-btn"
                  >
                    {c.primaryLabel}
                  </button>
                  {mode !== 'empty' && (
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={c.secondaryAction}
                      data-testid="shop-empty-secondary-btn"
                    >
                      {c.secondaryLabel}
                    </button>
                  )}
                </div>
              </div>
            );
          })()
        ) : (
          <>
            {/* List View */}
            {listingView === 'list' ? (
              <div className="product-list-view" dir={isArabic ? 'rtl' : 'ltr'}>
                {displayAds.map((ad) => {
                  const imageUrl = getImageUrl(ad);
                  const displayTitle = getAdTitle(ad, lang).text || ad.title;
                  const displayLocation = !lowBandwidth ? getLocalizedLocationLabel(ad, lang) : null;
                  return (
                    <ProductListCard
                      key={ad.ad_id}
                      product={ad}
                      imageUrl={imageUrl}
                      title={displayTitle}
                      price={formatMoney(ad.price, { locale: isArabic ? 'ar-SD' : 'en', currency: ad.currency || 'SDG' })}
                      location={displayLocation}
                      isFavorite={isFavorite(ad.ad_id)}
                      onOpen={() => navigate(`/ad/${ad.ad_id}`)}
                      onToggleFavorite={toggleFavorite}
                      onImageError={handleImageError}
                      onImageLoad={handleImageLoad}
                      badgeText={ad.hub === 'ladies' ? (`🧡 ${t('shopPage.ladiesHubBadge')}`) : null}
                      testId={`product-list-${ad.ad_id}`}
                    >
                      <BoostTimerBadge
                        isBoosted={ad.is_boosted}
                        boostedUntil={ad.boosted_until}
                        status={ad.status}
                        visibility={ad.visibility}
                        availability={ad.availability_status}
                        lang={lang}
                      />
                    </ProductListCard>
                  );
                })}
              </div>
            ) : (
              /* Grid View */
              <ProductGrid dir={isArabic ? 'rtl' : 'ltr'} className={lowBandwidth ? 'low-bandwidth' : ''}>
                {displayAds.map((ad) => {
                  const imageUrl = getImageUrl(ad);
                  const displayTitle = getAdTitle(ad, lang).text || ad.title;
                  const displayLocation = !lowBandwidth ? getLocalizedLocationLabel(ad, lang) : null;
                  return (
                    <ProductCard
                      key={ad.ad_id}
                      product={ad}
                      imageUrl={imageUrl}
                      title={displayTitle}
                      price={formatMoney(ad.price, { locale: isArabic ? 'ar-SD' : 'en', currency: ad.currency || 'SDG' })}
                      location={displayLocation}
                      isFavorite={isFavorite(ad.ad_id)}
                      onOpen={() => navigate(`/ad/${ad.ad_id}`)}
                      onToggleFavorite={toggleFavorite}
                      onImageError={handleImageError}
                      onImageLoad={handleImageLoad}
                      badgeText={ad.hub === 'ladies' ? (`🧡 ${t('shopPage.ladiesHubBadge')}`) : null}
                      testId={`product-${ad.ad_id}`}
                    >
                      <BoostTimerBadge
                        isBoosted={ad.is_boosted}
                        boostedUntil={ad.boosted_until}
                        status={ad.status}
                        visibility={ad.visibility}
                        availability={ad.availability_status}
                        lang={lang}
                      />
                      {!lowBandwidth && showDistance && buyerLocation && ad.latitude && ad.longitude && (
                        <div className="product-distance" data-testid={`distance-${ad.ad_id}`}>
                          {formatDistance(
                            calculateDistance(
                              buyerLocation.latitude,
                              buyerLocation.longitude,
                              ad.latitude,
                              ad.longitude
                            ),
                            isArabic
                          )}
                        </div>
                      )}
                    </ProductCard>
                  );
                })}
              </ProductGrid>
            )}
            
            {/* Load More / Pagination */}
            {hasMore && (
              <div className="load-more">
                <button 
                  className="btn-load-more"
                  onClick={handleLoadMore}
                  disabled={loading}
                >
                  {loading ? (t('loading')) : 
                    (t('shopPage.loadMore'))}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Recently Viewed — per-user, hidden when empty, has clear button. */}
      <RecentlyViewedCarousel />

      {/* Premium Sort Sheet */}
      <PremiumSortSheet

        open={sortOpen}
        onClose={() => setSortOpen(false)}
        value={sortBy}
        onChange={setSortBy}
        onApply={(key) => {
          setSortBy(key);
          // Refresh products with new sort
          setPage(1);
          fetchAds(1, true);
        }}
        onReset={() => {
          setSortBy('newest');
          setPage(1);
          fetchAds(1, true);
        }}
        isRTL={isRTL}
        variant="glass"
      />
    </div>
  );
}

export default Shop;
